import { useHasPermission, usePermittedDomains } from '../components/v2BaseComponents/Permissions/hooks';
import { getIntl } from '../utils/intl';
import useCareTeamMembers from './api/useCareTeamMembers';
import { useCollaborators } from './api/useCollaborators';
import useMedicalProfileData from './api/useMedicalProfileData';
import useMedicalProfileFiles from './api/useMedicalProfileFiles';
import useNotes from './api/useNotes';
import useProfileConnectedHealthSystems from './api/useProfileConnectedHealthSystems';
import useFastenConnections from './api/useFastenConnections';
import { useReports } from './api/useReports';

const intl = getIntl('ProfileCardButtons', {
    careTeam: 'Care Team',
    profileInfo: 'Profile Info',
    activeCount: 'Active',
    upcomingCount: 'Upcoming',
    totalCount: 'Total',
    reports: 'Reports',
    files: 'Files',
    chat: 'Chat Assistant',
    lab: 'Labs',
    chatSubtext: '(Beta)',
    activity: 'Activity',
    restricted: 'Restricted',
    portals: 'Portals',
    collaborators: 'Collaborators',
});

export type UseProfileCountsArgs = {
    profileId: string | null;
    emptyForNoAccess?: boolean;
};

export type UseProfileCountsReturnValue = {
    visit: string;
    medication: string;
    condition: string;
    allergy: string;
    imaging: string;
    procedure: string;
    vaccination: string;
    lab: string;
    careTeam: string;
    notes: string;
    files: string;
    portals: string;
    collaborators: string;
    reports: string;
};

export const useProfileCounts = ({
    profileId,
    emptyForNoAccess,
}: UseProfileCountsArgs): UseProfileCountsReturnValue => {
    const { isLoadingResourceCounts, resourceCounts } = useMedicalProfileData({ medicalProfileId: profileId });
    const { isLoadingCareTeamMembers, careTeamMembers } = useCareTeamMembers({
        medicalProfileId: profileId,
    });
    const { collaborators, isLoadingCollaborators } = useCollaborators({ medicalProfileId: profileId });
    const { notes } = useNotes({
        noteQueryArgs: { medicalProfileId: profileId, entityType: 'medical-profile' },
    });
    const { isLoadingFolderMetadata, fileCount } = useMedicalProfileFiles({ medicalProfileId: profileId });
    const { connectedHealthSystems, isLoading: isLoadingConnectedHealthSystems } = useProfileConnectedHealthSystems({
        medicalProfileId: profileId,
    });
    const hasPortalViewPermission = useHasPermission(profileId, 'patientPortals', 'view');
    const { connections: fastenConnections, isLoading: isLoadingFastenConnections } = useFastenConnections({
        medicalProfileId: profileId || '',
        enabled: hasPortalViewPermission,
    });
    const { isLoadingReports, isLoadingSharedReports, sharedReports, reports } = useReports({
        medicalProfileId: profileId,
    });

    const getCountMessage = (base: string, isLoading: boolean, count?: number, access?: boolean) => {
        if (isLoading) return `${base} (...)`;
        if (access) {
            return `${base} (${count || 0})`;
        } else {
            return emptyForNoAccess ? '' : `${base} (${intl('restricted')})`;
        }
    };

    const permittedDomains = usePermittedDomains(
        profileId,
        [
            'visits',
            'medications',
            'conditions',
            'allergies',
            'imaging',
            'procedures',
            'vaccinations',
            'labs',
            'careTeam',
            'files',
            'patientPortals',
            'reports',
        ],
        'view',
    );

    return {
        visit: getCountMessage(
            intl('upcomingCount'),
            isLoadingResourceCounts,
            resourceCounts?.filtered?.upcomingVisitCount,
            permittedDomains.includes('visits'),
        ),
        medication: getCountMessage(
            intl('activeCount'),
            isLoadingResourceCounts,
            resourceCounts?.filtered?.activeMedicationCount,
            permittedDomains.includes('medications'),
        ),
        condition: getCountMessage(
            intl('activeCount'),
            isLoadingResourceCounts,
            resourceCounts?.filtered?.activeConditionCount,
            permittedDomains.includes('conditions'),
        ),
        allergy: getCountMessage(
            intl('activeCount'),
            isLoadingResourceCounts,
            resourceCounts?.filtered?.activeAllergyCount,
            permittedDomains.includes('allergies'),
        ),
        imaging: getCountMessage(
            intl('totalCount'),
            isLoadingResourceCounts,
            resourceCounts?.filtered?.imagingCount,
            permittedDomains.includes('imaging'),
        ),
        procedure: getCountMessage(
            intl('totalCount'),
            isLoadingResourceCounts,
            resourceCounts?.filtered?.procedureCount,
            permittedDomains.includes('procedures'),
        ),
        vaccination: getCountMessage(
            intl('totalCount'),
            isLoadingResourceCounts,
            resourceCounts?.filtered?.vaccinationCount,
            permittedDomains.includes('vaccinations'),
        ),
        lab: getCountMessage(
            intl('totalCount'),
            isLoadingResourceCounts,
            resourceCounts?.filtered?.labCount,
            permittedDomains.includes('labs'),
        ),
        careTeam: getCountMessage(
            intl('totalCount'),
            isLoadingCareTeamMembers,
            careTeamMembers?.length,
            permittedDomains.includes('careTeam'),
        ),
        notes: getCountMessage(intl('totalCount'), false, notes?.notes?.length, true),
        files: getCountMessage(
            intl('totalCount'),
            isLoadingFolderMetadata,
            fileCount || 0,
            permittedDomains.includes('files'),
        ),
        portals: getCountMessage(
            intl('totalCount'),
            isLoadingConnectedHealthSystems || isLoadingFastenConnections,
            (connectedHealthSystems?.length || 0) + (fastenConnections?.length || 0),
            permittedDomains.includes('patientPortals'),
        ),
        collaborators: getCountMessage(
            intl('totalCount'),
            isLoadingCollaborators,
            collaborators.length,
            permittedDomains.includes('careTeam'),
        ),
        reports: getCountMessage(
            intl('totalCount'),
            isLoadingReports || isLoadingSharedReports,
            reports?.length + sharedReports?.length,
            permittedDomains.includes('reports'),
        ),
    };
};
